import { type ReactNode } from "react";
import { useMandateSummary } from "../hooks/useMandateSummary.ts";

// Same hardcoded palette as the sibling cards.
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const BLUE = "#6699ff"; // realized portion of the split bar
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// Adverse-selection band on the impact share (backend labels.classify_toxicity).
// The word "toxicity" is NEVER shown; these are the neutral institutional labels.
const BAND: Record<string, { text: string; color: string }> = {
  low: { text: "spread largely retained", color: GREEN },
  elevated: { text: "elevated impact share", color: MUTED },
  high: { text: "impact-weighted", color: AMBER },
  unknown: { text: "n/a", color: MUTED },
};

function fmt(n: number | null | undefined, digits = 2): string {
  return n == null ? "—" : n.toFixed(digits);
}

const cardStyle = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "12px 14px",
} as const;

const headRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8,
} as const;

const dividerFoot = {
  fontSize: 11,
  color: MUTED,
  marginTop: 8,
  borderTop: `1px solid ${BORDER}`,
  paddingTop: 7,
} as const;

export function AdverseSelectionCard({
  venue,
  pair,
  clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useMandateSummary({ venue, pair, clientId });
  const as = q.data?.adverse_selection;

  const errorFooter = q.isError ? (
    <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>could not load</div>
  ) : null;

  let chip: ReactNode;
  let body: ReactNode;

  // ---- State 3: no data --------------------------------------------------
  if (!as || as.n === 0 || as.effective_bps == null) {
    chip = <span style={{ fontSize: 11, color: MUTED }}>n/a</span>;
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 500, color: MUTED }}>—</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
          No usable trades in the window.
        </div>
      </>
    );
  } else {
    const eff = as.effective_bps;
    const rlz = as.realized_bps ?? 0;
    const imp = as.impact_bps ?? 0;
    const share = as.impact_share;

    // ---- State 2: non-positive effective spread (share undefined) --------
    // Real numbers shown; NO split bar (a proportional split is meaningless on
    // non-positive / signed components). The explanatory text carries the read.
    if (share == null || eff <= 0) {
      // Two status-driven sub-states (server-set impact_share_status):
      //  low_effective          -> share unstable (effective near zero)
      //  non_positive_effective -> share undefined (effective <= 0)
      const lowEff = as.impact_share_status === "low_effective";
      chip = (
        <span style={{ fontSize: 11, color: MUTED }}>
          {lowEff ? "share unstable" : "share n/a"}
        </span>
      );
      body = (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{
                fontSize: 24,
                fontWeight: 500,
                color: TEXT,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmt(eff, 1)}
            </span>
            <span style={{ fontSize: 12, color: MUTED }}>bps effective</span>
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
            {lowEff
              ? "Share unstable, effective spread near zero \u2014 the impact / effective ratio is not meaningful at this denominator."
              : "Share unavailable for non-positive effective spread."}
          </div>
          <div style={{ ...dividerFoot, fontVariantNumeric: "tabular-nums" }}>
            <span style={{ color: BLUE }}>realized {fmt(rlz)}</span> ·{" "}
            <span style={{ color: AMBER }}>impact {fmt(imp)}</span> · n{" "}
            {as.n.toLocaleString()}
          </div>
        </>
      );
    } else if (rlz >= 0 && imp >= 0) {
      // ---- State 1a: clean positive partition (effective > 0, both
      // components >= 0) -- the proportional split bar is faithful here.
      const band = BAND[as.toxicity ?? "unknown"] ?? BAND.unknown;
      const sharePct = share * 100;
      const impPct = Math.max(0, Math.min(100, sharePct));
      const rlzPct = 100 - impPct;

      chip = (
        <span
          style={{
            fontSize: 11,
            color: band.color,
            border: `1px solid ${band.color}55`,
            padding: "2px 8px",
            borderRadius: 5,
          }}
        >
          {band.text}
        </span>
      );
      body = (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{
                fontSize: 24,
                fontWeight: 500,
                color: TEXT,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {sharePct.toFixed(0)}%
            </span>
            <span style={{ fontSize: 12, color: MUTED }}>impact share</span>
          </div>

          <div
            style={{
              display: "flex",
              height: 8,
              borderRadius: 4,
              overflow: "hidden",
              marginTop: 10,
              background: "rgba(255,255,255,0.05)",
            }}
            title={`realized ${rlzPct.toFixed(0)}% · impact ${impPct.toFixed(0)}% of effective spread`}
          >
            <div style={{ width: `${rlzPct}%`, background: BLUE }} />
            <div style={{ width: `${impPct}%`, background: AMBER }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
            <span style={{ fontSize: 11, color: BLUE }}>realized {fmt(rlz)}</span>
            <span style={{ fontSize: 11, color: AMBER }}>impact {fmt(imp)}</span>
          </div>

          <div style={{ ...dividerFoot, color: band.color }}>
            {band.text}
            <span style={{ color: MUTED, fontVariantNumeric: "tabular-nums" }}>
              {" "}· effective {fmt(eff)} bps · n {as.n.toLocaleString()}
            </span>
          </div>
        </>
      );
    } else {
      // ---- State 1b: share defined (effective > 0) but a component is
      // negative -- the share is valid and meaningful, but a proportional
      // split bar cannot represent it (parts don't form a positive partition).
      // Lead with the share %, show no bar, explain. NOT "share n/a".
      const sharePct = share * 100;
      chip = (
        <span
          style={{
            fontSize: 11,
            color: AMBER,
            border: `1px solid ${AMBER}55`,
            padding: "2px 8px",
            borderRadius: 5,
          }}
        >
          impact exceeds spread
        </span>
      );
      body = (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{
                fontSize: 24,
                fontWeight: 500,
                color: TEXT,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {sharePct.toFixed(0)}%
            </span>
            <span style={{ fontSize: 12, color: MUTED }}>impact share</span>
          </div>

          <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
            Impact exceeds effective spread because realized capture is negative
            over the horizon; the components do not form a simple proportional
            split.
          </div>

          <div style={{ ...dividerFoot, fontVariantNumeric: "tabular-nums" }}>
            <span style={{ color: BLUE }}>realized {fmt(rlz)}</span> ·{" "}
            <span style={{ color: AMBER }}>impact {fmt(imp)}</span> · effective{" "}
            {fmt(eff)} bps · n {as.n.toLocaleString()}
          </div>
        </>
      );
    }
  }

  return (
    <div style={cardStyle}>
      <div style={headRow}>
        <span style={{ fontSize: 12, color: MUTED }}>Adverse selection</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: MUTED }}>30d</span>
          {chip}
        </span>
      </div>
      {body}
      {errorFooter}
    </div>
  );
}
